<template>
    <div class="rating-card">
        <div class="rating-card__header" :class="{ 'rating-card__header--with-select': showScopeSelect || showMetricSelect }">
            <div class="rating-card__header-left">
                <select
                    v-if="showScopeSelect"
                    class="rating-card__scope"
                    :value="scope"
                    :disabled="disabled"
                    @change="$emit('update:scope', $event.target.value)"
                >
                    <option
                        v-for="option in scopeOptions"
                        :key="option.value"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </option>
                </select>
                <div class="rating-card__title">{{ title }}</div>
            </div>
            <select
                v-if="showMetricSelect"
                class="rating-card__metric"
                :value="metric"
                :disabled="disabled"
                @change="$emit('update:metric', $event.target.value)"
            >
                <option
                    v-for="option in metricOptions"
                    :key="option.value"
                    :value="option.value"
                >
                    {{ option.label }}
                </option>
            </select>
        </div>
        <div class="rating-card__list">
            <div
                v-for="player in players"
                :key="(player.player_id || player.number) + '-' + player.rank"
                class="rating-card__player"
            >
                <div class="rating-card__number">
                    {{ player.number ?? '—' }}
                </div>

                <div class="rating-card__info">
                    <div class="rating-card__name">{{ player.name }}</div>
                    <div class="rating-card__team">{{ player.team }}</div>
                </div>

                <div class="rating-card__score">
                    {{ player.rating }}
                </div>

                <div
                    v-if="player.rank"
                    class="rating-card__rank"
                    :class="rankClass(player.rank)"
                >
                    {{ player.rank }}
                </div>
            </div>

            <div v-if="!players || players.length === 0" class="rating-card__empty">
                Немає даних
            </div>
        </div>
    </div>
</template>

<script>
export const COMPETITION_SCOPE_OPTIONS = [
    { value: 'championship', label: 'чемпіонат' },
    { value: 'region', label: 'область' }
]

export const GPS_RATING_METRIC_OPTIONS = [
    { value: 'total_distance', label: 'Загальна дистанція' },
    { value: 'zone_4', label: 'Зона 4' },
    { value: 'zone_5', label: 'Зона 5' },
    { value: 'zone_6', label: 'Зона 6' },
    { value: 'speed_distance', label: 'Швидкісна дист.' },
    { value: 'average_speed', label: 'Середня швидкість' },
    { value: 'max_speed', label: 'Макс. швидкість' },
    { value: 'sprints', label: 'Спринти' },
    { value: 'starts', label: 'Старт' },
    { value: 'stops', label: 'Стоп' }
]

export default {
    name: 'PlayerRatingList',

    props: {
        title: {
            type: String,
            required: true
        },
        players: {
            type: Array,
            required: true
        },
        showScopeSelect: {
            type: Boolean,
            default: false
        },
        scope: {
            type: String,
            default: 'championship'
        },
        scopeOptions: {
            type: Array,
            default: () => COMPETITION_SCOPE_OPTIONS
        },
        showMetricSelect: {
            type: Boolean,
            default: false
        },
        metric: {
            type: String,
            default: 'total_distance'
        },
        metricOptions: {
            type: Array,
            default: () => GPS_RATING_METRIC_OPTIONS
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    emits: ['update:metric', 'update:scope'],

    methods: {
        rankClass(rank) {
            if (rank === 1) return 'rating-card__rank--gold'
            if (rank === 2) return 'rating-card__rank--silver'
            if (rank === 3) return 'rating-card__rank--bronze'
            return 'rating-card__rank--default'
        }
    }
}
</script>

<style scoped>
.rating-card {
    background: linear-gradient(145deg, #2a2b2b 0%, #252626 100%);
    border: 1px solid #3a3b3b;
    border-radius: 14px;
    padding: 14px 16px 12px;
    height: 100%;
}
.rating-card__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
}
.rating-card__header--with-select {
    position: relative;
    align-items: center;
    min-height: 28px;
}
.rating-card__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    width: 100%;
    justify-content: center;
}
.rating-card__header--with-select .rating-card__header-left {
    justify-content: center;
}
.rating-card__header--with-select .rating-card__scope {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
}
.rating-card__title {
    color: #C59014;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.3px;
    white-space: nowrap;
}
.rating-card__scope {
    background: #333;
    color: #a68a3a;
    border: 1px solid #4a4a4a;
    border-radius: 8px;
    padding: 3px 8px;
    font-size: 12px;
    outline: none;
    cursor: pointer;
    max-width: 110px;
}
.rating-card__scope:disabled {
    opacity: 0.5;
    cursor: default;
}
.rating-card__metric {
    background: #3A3B3B;
    color: #BD9135;
    font-size: 12px;
    border: 1px solid #C59014;
    border-radius: 6px;
    padding: 3px 8px;
    outline: none;
    cursor: pointer;
    max-width: 100%;
}
.rating-card__metric:disabled {
    opacity: 0.5;
    cursor: default;
}
.rating-card__list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.rating-card__player {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 10px;
    transition: background 0.15s;
}
.rating-card__player:hover {
    background: rgba(197, 144, 20, 0.08);
}
.rating-card__number {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: linear-gradient(135deg, #3a3b3b 0%, #333 100%);
    border: 1px solid #484848;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #C59014;
    font-weight: 700;
    font-size: 14px;
    flex-shrink: 0;
}
.rating-card__info {
    flex: 1;
    min-width: 0;
}
.rating-card__name {
    color: #eee;
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.rating-card__team {
    color: #666;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.rating-card__score {
    color: #f0e6cc;
    font-weight: 700;
    font-size: 16px;
    margin-right: 4px;
    font-variant-numeric: tabular-nums;
}
.rating-card__rank {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 11px;
    flex-shrink: 0;
}
.rating-card__rank--gold {
    background: linear-gradient(135deg, #C59014, #d4a42a);
    box-shadow: 0 0 8px rgba(197, 144, 20, 0.4);
}
.rating-card__rank--silver {
    background: linear-gradient(135deg, #78909c, #90a4ae);
}
.rating-card__rank--bronze {
    background: linear-gradient(135deg, #a1887f, #8d6e63);
}
.rating-card__rank--default {
    background: #4a4a4a;
}
.rating-card__empty {
    color: #666;
    font-size: 13px;
    text-align: center;
    padding: 16px 0;
}
</style>
